import {
  DraftBusinessForm,
  PayoutDetails,
  PayoutForm,
  RequestSettlementInput,
  SettleRequestPayload,
} from "../../../interfaces/OnboardingObject";
import { normalizePhone } from "../../helpers/PayoutHelper";
import { cleanPhoneList } from "../../helpers/PhoneListHelper";
import { VISITING_CARD_MAX_PHONES } from "../../../interfaces/VisitingCardObject";

/**
 * Version of the businessPayload shape we emit. The backend's approval mapper
 * keys off this — bump it (and the payload below) together, never separately.
 */
export const ONBOARDING_PAYLOAD_VERSION = 2;

/**
 * DraftBusinessForm → create-pending-business wire payload.
 *
 * Rules from the API handover doc:
 * - `finalFee` is NEVER sent — the server echoes it back equal to chargedAmount.
 * - `chargedAmount` is the exact ₹ price the captain is charging (it replaced
 *   the old `discountPercent`). Server validates >= 0 and <= onboardingFee.
 * - No captainId — identity comes from the bearer token.
 * - Required businessPayload keys: address, listingType, latitude, longitude
 *   (since v1) and visitingCard { phones (1-3), address, email? } (since v2).
 *   Client validates before submit; server re-validates fail-fast.
 * - businessSize (S/M/L) is a top-level field, sibling to chargedAmount,
 *   NOT nested inside businessPayload.
 * - `ownerId` (businessPayload) is resolved via phone-number lookup and
 *   required client-side (see CreateDraftBusinessModal's validate()), but is
 *   NOT part of the documented required-keys list above — whether the
 *   server persists it through approval hasn't been confirmed yet.
 *
 * - `nineSetupId` is a REQUIRED top-level field — the id of the `nine_setup`
 *   row this draft came from. Confirmed 2026-08-02 from the server's own
 *   rejection: `VALIDATION_ERROR / "nineSetupId is required"`. It was briefly
 *   sent as `setupId` nested inside businessPayload, which the server ignored.
 *
 *   ⚠️ Because it is REQUIRED, every draft must now originate from a nine_setup
 *   row. The Onboarding tab's own "New draft" button has no setup behind it and
 *   will fail this validation — see DraftBusinessSection.
 */
export function buildCreatePendingBusinessPayload(form: DraftBusinessForm) {
  return {
    businessName: form.name.trim(),
    contactPhone: form.phone,
    chargedAmount: form.chargedAmount,
    businessSize: form.businessSize,
    nineSetupId: form.setupId,
    payloadVersion: ONBOARDING_PAYLOAD_VERSION,
    businessPayload: {
      address: form.address.trim(),
      listingType: form.listingType,
      description: form.description?.trim() || undefined,
      contactEmail: form.contactEmail?.trim() || undefined,
      latitude: form.latitude ?? null,
      longitude: form.longitude ?? null,
      ownerId: form.ownerId,
      gstin: form.gstin?.trim() || undefined,
      visitingCard: {
        phones: cleanPhoneList(form.visitingCard.phones, VISITING_CARD_MAX_PHONES),
        address: form.visitingCard.address.trim(),
        email: form.visitingCard.email?.trim() || undefined,
      },
      // Always sent — billingAddress is required independent of gstin.
      billingAddress: {
        line1: form.billingAddress.line1.trim(),
        line2: form.billingAddress.line2?.trim() || undefined,
        city: form.billingAddress.city.trim(),
        state: form.billingAddress.state,
        pincode: form.billingAddress.pincode.trim(),
        country: form.billingAddress.country,
      },
    },
  };
}

/**
 * Payment-link retry payload — just the pendingId.
 *
 * PREVIOUSLY this re-called create-pending-business with the same
 * businessName + contactPhone, relying on the server's idempotent-duplicate
 * catch path to return the same row with a fresh link. That doesn't work:
 * the server checks "does this nine_setup already have a PENDING pending_business"
 * BEFORE it ever reaches the duplicate-insert handling, and that check now
 * always fires first (deterministically, since the first submission's row
 * already exists) — so a retry landed as a hard NINE_SETUP_ALREADY_HAS_PENDING_BUSINESS
 * rejection, not a graceful "fresh link" response. Retrying also required
 * businessSize/businessPayload to satisfy the create-path's validation with no
 * source of truth for them post-submit.
 *
 * The server now exposes a dedicated retry-payment-link endpoint keyed only
 * on pendingId, which sidesteps all of that — see OnboardingApiService.retryPaymentLink.
 */
export function buildRetryPaymentLinkPayload(pendingId: string) {
  return { pendingId };
}

/** Payout form → wire PayoutDetails. Only the chosen method's fields are sent;
 *  values are canonicalised the same way the server does (it re-validates). */
export function buildPayoutDetailsPayload(form: PayoutForm): PayoutDetails {
  const phone = normalizePhone(form.phone) || null;
  return form.method === "UPI"
    ? { method: "UPI", upiId: form.upiId.trim().toLowerCase(), phone }
    : {
        method: "BANK",
        holder: form.holder.trim(),
        account: form.account.replace(/\s/g, ""),
        ifsc: form.ifsc.trim().toUpperCase(),
        phone,
      };
}

/** request-settlement body. Omitted keys stay omitted (older server tolerant). */
export function buildRequestSettlementPayload(input: RequestSettlementInput) {
  const body: { note?: string; payout?: PayoutDetails } = {};
  const note = input.note?.trim();
  if (note) body.note = note;
  if (input.payout) body.payout = buildPayoutDetailsPayload(input.payout);
  return body;
}

/** Admin settle body: blank reference/note are dropped, not sent as "". */
export function buildSettleRequestPayload(input: SettleRequestPayload) {
  return {
    expectedAmount: input.expectedAmount,
    reference: input.reference?.trim() || undefined,
    note: input.note?.trim() || undefined,
  };
}

export default {
  buildCreatePendingBusinessPayload,
  buildRetryPaymentLinkPayload,
  buildPayoutDetailsPayload,
  buildRequestSettlementPayload,
  buildSettleRequestPayload,
  ONBOARDING_PAYLOAD_VERSION,
};
